// File: src/EnglishTool/quizHelpers.js

import { shuffleArray } from "./shuffleArray.js";
import { getNounsPool } from "./pool/nounsPool.js";
import { getVerbsPool } from "./pool/verbsPool.js";
import { getAdjectivesPool } from "./pool/adjectivesPool.js";
import { getAdverbsPool } from "./pool/adverbsPool.js";

export function asWord(x) {
  if (typeof x === "string") return x;
  if (x && typeof x.word === "string") return x.word;
  return "";
}

/** UTILS **/
export function capitalize(str) {
  return str ? str[0].toUpperCase() + str.slice(1) : "";
}

// Words that are both common nouns AND verbs, or are ambiguous in MC questions
const AMBIGUOUS_NOUN_VERBS = [
  "volunteer", "attempt", "play", "run", "watch", "plan", "present", "cook", "dream", "call", "test", "help", "fight", "change", "visit", "order"
  // ...expand as you find more
];
/** =============== NOUNS =============== **/

// MC: Which of these is a [nounType] noun?
export function buildNounTypeMC(nounPool, nounType) {
  const corrects = nounPool.filter(n => n.nounType === nounType);
  const wrongs = nounPool.filter(n => n.nounType !== nounType);
  if (!corrects.length || wrongs.length < 3) return null;

  const correctObj = shuffleArray(corrects)[0];
  const correct = asWord(correctObj);

  // Ensure all distractors are stringified, even if pool changes in future!
  const distractors = shuffleArray(wrongs)
    .slice(0, 3)
    .map(asWord);

  return {
    questionType: "MC",
    question: `Which of these is a ${nounType} noun?`,
    correctAnswer: correct,
    options: shuffleArray([correct, ...distractors]),
  };
}


// MC: What type of noun is "___"?
export function buildNounClassifyMC(nounPool) {
  const item = shuffleArray(nounPool)[0];
  // Collect all unique noun types from the pool (forward compatible)
  const allTypes = Array.from(new Set(nounPool.map(n => n.nounType)));
  // Ensure the correct answer is present (capitalize all for display)
  let options = allTypes.map(capitalize);
  // Add up to 4 distractors, pad with classic options if needed
  while (options.length < 4) {
    for (let extra of ["Common", "Proper", "Abstract", "Concrete", "Collective", "Possessive"]) {
      if (!options.includes(extra)) options.push(extra);
      if (options.length === 4) break;
    }
  }
  options = shuffleArray(options).slice(0, 4);
  // Ensure correct answer is always included
  if (!options.includes(capitalize(item.nounType))) {
    options[0] = capitalize(item.nounType);
    options = shuffleArray(options);
  }
  return {
    questionType: "MC",
    question: `What type of noun is "${item.word}"?`,
    correctAnswer: capitalize(item.nounType),
    options,
  };
}


// IDENTIFY: Find all nouns in a sentence
export function buildIdentifyNounsQ(sentencePool) {
  const nounSentences = sentencePool.filter(s => s.focus === "nouns");
  if (!nounSentences.length) return null;
  const item = shuffleArray(nounSentences)[0];
  return {
    questionType: "IDENTIFY",
    question: `Identify all the nouns in the sentence: "${item.sentence}"`,
    correctAnswers: item.correctAnswers,
  };
}

// FITB: Use [noun] in a sentence
export function buildFillInBlankNoun(item) {
  return {
    questionType: "FITB",
    question: `Use "${item.word}" in a sentence. Type your sentence below:`,
    correctAnswer: item.word,
    checkType: "keyword-in-sentence",
    requiredWord: item.word
  };
}

/** =============== VERBS =============== **/

// MC: Which of these is a verb?
export function buildVerbMC(verbPool, grade) {
  const verbList = verbPool
    .map(v => typeof v === "string" ? v : v.word)
    .filter(Boolean)
    // EXCLUDE ambiguous words for MC context!
    .filter(w => !AMBIGUOUS_NOUN_VERBS.includes(w.toLowerCase()));
  if (!verbList.length) return null; // defensive for empty pool

  const correct = shuffleArray(verbList)[0];
  // Distractor pool (remains same as before)
  let distractorSource = [
    ...getNounsPool(grade).map(n => n.word),
    ...getAdjectivesPool(grade).map(a => a.word),
    ...getAdverbsPool(grade).map(a => a.word)
  ].filter(Boolean);
  let distractors = getDistractors(distractorSource, correct);
  // Always 4 options, all defined, all unique
  const options = shuffleArray([correct, ...distractors]).filter((v, i, arr) => !!v && arr.indexOf(v) === i);
  return {
    questionType: "MC",
    question: "Which of the following is a verb?",
    correctAnswer: correct,
    options,
  };
}


// FITB: Use [verb] in a sentence
export function buildFillInBlankVerb(item) {
  const word = typeof item === "string" ? item : item.word;
  return {
    questionType: "FITB",
    question: `Write a sentence using the verb "${word}":`,
    correctAnswer: word,
    checkType: "keyword-in-sentence",
    requiredWord: word,
  };
}

// IDENTIFY: Find all verbs in a sentence
export function buildIdentifyVerbsQ(sentencePool) {
  const verbSentences = sentencePool.filter(s => s.focus === "verbs");
  if (!verbSentences.length) return null;
  const item = shuffleArray(verbSentences)[0];
  return {
    questionType: "IDENTIFY",
    question: `Identify all the verbs in the sentence: "${item.sentence}"`,
    correctAnswers: item.correctAnswers,
  };
}

// MC: What tense is "walked"?  (This is simple, assumes pool includes tense property)
export function buildVerbTenseMC(verbPool) {
  const item = shuffleArray(verbPool)[0];
  // This requires your verbPool items to have a 'tense' field, otherwise skip or set to present
  if (!item || !item.tense) return null;
  const tenses = ["present", "past", "future"];
  return {
    questionType: "MC",
    question: `What tense is "${item.word}"?`,
    correctAnswer: capitalize(item.tense),
    options: shuffleArray(tenses.map(capitalize)),
  };
}

// MC: Which phrase is a participial adjective?
export function buildParticipialAdjectiveMC() {
  return {
    questionType: "MC",
    question: "Which phrase is a participial adjective?",
    correctAnswer: "shivering",
    options: shuffleArray([
      "shivering",
      "the puppy",
      "under the blanket",
      "delicious"
    ]),
  };
}

// MC: Which sentence contains a predicative adjective?
export function buildPredicativeAdjectiveMC() {
  return {
    questionType: "MC",
    question: "Which sentence contains a predicative adjective?",
    correctAnswer: "The building is old.",
    options: shuffleArray([
      "The building is old.",
      "The old building collapsed.",
      "Building an old house is hard.",
      "He is in the building."
    ]),
  };
}

// MC: Which is a non-restrictive adjective clause?
export function buildNonRestrictiveClauseMC() {
  return {
    questionType: "MC",
    question: "Which sentence uses a non-restrictive adjective clause?",
    correctAnswer: "My teacher, who loves math, is kind.",
    options: shuffleArray([
      "My teacher, who loves math, is kind.",
      "The teacher who loves math is kind.",
      "My teacher loves math.",
      "Who loves math is kind."
    ]),
  };
}

// IDENTIFY: Identify the participial phrase in a sentence
export function buildIdentifyParticipialPhraseQ() {
  return {
    questionType: "IDENTIFY",
    question: `Identify the participial phrase in the sentence: "The students, excited by the field trip, boarded the bus."`,
    correctAnswers: ["excited by the field trip"],
  };
}


/** =============== ADJECTIVES =============== **/

// MC: Which of these is an adjective?
export function buildAdjectiveMC(adjPool, grade) {
  const adjList = adjPool.map(a => typeof a === "string" ? a : a.word);
  const correct = shuffleArray(adjList)[0];
  let distractorSource = [
    ...getNounsPool(grade).map(n => n.word),
    ...getVerbsPool(grade).map(v => typeof v === "string" ? v : v.word),
    ...getAdverbsPool(grade).map(a => a.word),
  ];
  let distractors = getDistractors(distractorSource, correct);
  return {
    questionType: "MC",
    question: "Which of the following is an adjective?",
    correctAnswer: correct,
    options: shuffleArray([correct, ...distractors]),
  };
}

// FITB: Write a sentence with [adjective]
export function buildFillInBlankAdjective(item) {
  const word = typeof item === "string" ? item : item.word;
  return {
    questionType: "FITB",
    question: `Write a sentence with the adjective "${word}":`,
    correctAnswer: word,
    checkType: "keyword-in-sentence",
    requiredWord: word,
  };
}

// IDENTIFY: Find all adjectives in a sentence
export function buildIdentifyAdjectivesQ(sentencePool) {
  const adjSentences = sentencePool.filter(s => s.focus === "adjectives");
  if (!adjSentences.length) return null;
  const item = shuffleArray(adjSentences)[0];
  return {
    questionType: "IDENTIFY",
    question: `Identify all the adjectives in the sentence: "${item.sentence}"`,
    correctAnswers: item.correctAnswers,
  };
}


/** =============== ADVERBS =============== **/

// MC: Which of these is an adverb?
export function buildAdverbMC(advPool, grade) {
  const advList = advPool.map(a => typeof a === "string" ? a : a.word);
  const correct = shuffleArray(advList)[0];
  let distractorSource = [
    ...getNounsPool(grade).map(n => n.word),
    ...getVerbsPool(grade).map(v => typeof v === "string" ? v : v.word),
    ...getAdjectivesPool(grade).map(a => a.word),
  ];
  let distractors = getDistractors(distractorSource, correct);
  return {
    questionType: "MC",
    question: "Which of the following is an adverb?",
    correctAnswer: correct,
    options: shuffleArray([correct, ...distractors]),
  };
}

// MC: Which part of this sentence is an adverb clause?
export function buildAdverbClauseMC() {
  return {
    questionType: "MC",
    question: "Which part of this sentence is an adverb clause? 'She left because she was late.'",
    correctAnswer: "because she was late",
    options: shuffleArray([
      "because she was late",
      "She left",
      "late",
      "she was late"
    ])
  };
}

// IDENTIFY: Identify the adverb clause in a sentence
export function buildIdentifyAdverbClauseQ() {
  return {
    questionType: "IDENTIFY",
    question: `Identify the adverb clause in the sentence: "He finished the project after he got home."`,
    correctAnswers: ["after he got home"],
  };
}

// FITB: Write a sentence with [adverb]
export function buildFillInBlankAdverb(item) {
  const word = typeof item === "string" ? item : item.word;
  return {
    questionType: "FITB",
    question: `Write a sentence with the adverb "${word}":`,
    correctAnswer: word,
    checkType: "keyword-in-sentence",
    requiredWord: word,
  };
}

// IDENTIFY: Find all adverbs in a sentence
export function buildIdentifyAdverbsQ(sentencePool) {
  const advSentences = sentencePool.filter(s => s.focus === "adverbs");
  if (!advSentences.length) return null;
  const item = shuffleArray(advSentences)[0];
  return {
    questionType: "IDENTIFY",
    question: `Identify all the adverbs in the sentence: "${item.sentence}"`,
    correctAnswers: item.correctAnswers,
  };
}

export function buildIntensifierMC() {
  return {
    questionType: "MC",
    question: "Which of these words is an intensifier?",
    correctAnswer: "extremely",
    options: shuffleArray([
      "extremely",
      "slowly",
      "yesterday",
      "table"
    ])
  };
}

export function buildComparativeSuperlativeAdverbMC() {
  return {
    questionType: "MC",
    question: "Which option shows the comparative and superlative forms of 'well'?",
    correctAnswer: "better, best",
    options: shuffleArray([
      "better, best",
      "well, good",
      "good, better",
      "well, best"
    ])
  };
}

export function buildAdverbPlacementMC() {
  return {
    questionType: "MC",
    question: "Where can the adverb go in this sentence? (Choose all that apply)\n'I _____ usually walk to school.'",
    correctAnswer: "Before or after the verb",
    options: shuffleArray([
      "Before or after the verb",
      "Only at the beginning",
      "Only at the end",
      "Never in the sentence"
    ])
  };
}

// --- Utility for full word, flexible matching ---
// Utility: Required Word Check (for Fill-in-the-Blank)
export function checkRequiredWord(userInput, requiredWord, allowForms = false) {
  if (!userInput || !requiredWord) return false;
  // Remove punctuation (except apostrophes), lowercase everything
  const sanitized = userInput.replace(/[.,!?;:"()[\]{}]/g, "").toLowerCase();
  const required = requiredWord.trim().toLowerCase();

  // Allow slight forms for verbs only (e.g., run/runs/running/ran)
  if (allowForms) {
    // You can improve this pattern for more verb tenses if desired!
    const pattern = new RegExp(`\\b${required}(s|d|ing|en|es)?\\b`, "i");
    return pattern.test(sanitized);
  } else {
    // Exact match, but punctuation is removed, so "library" will match "library."
    const words = sanitized.split(/\s+/);
    return words.includes(required);
  }
}


export function checkSentenceStructure(userInput) {
  let errors = [];
  if (userInput.trim().split(/\s+/).length < 4) {
    errors.push("Write a complete sentence (at least 4 words).");
  }
  if (!/[.?!]$/.test(userInput.trim())) {
    errors.push("End your sentence with a period, question mark, or exclamation mark.");
  }
  if (!/^[A-Z]/.test(userInput.trim())) {
    errors.push("Start your sentence with a capital letter.");
  }
  return errors;
}

// Dynamic sample answer generator (by type)
export function makeSampleSentence(word, type) {
  switch(type) {
    case "noun":      return `The ${word} is important in this story.`;
    case "verb":      return [`I like to ${word} my bike.`, `Do you know how to ${word} a bike?`][Math.floor(Math.random()*2)];
    case "adjective": return `It was a very ${word} day at school.`;
    case "adverb":    return `She finished her homework ${word}.`;
    default:          return `Try using "${word}" in a sentence.`;
  }
}

// Multiple-choice: Which part is the appositive?
export function buildAppositiveMC() {
  return {
    questionType: "MC",
    question: "Which part of this sentence is the appositive? 'My brother, a talented artist, drew this.'",
    correctAnswer: "a talented artist",
    options: shuffleArray([
      "a talented artist",
      "My brother",
      "drew this",
      "the sentence has no appositive"
    ])
  };
}
 
const DEFAULT_DISTRACTORS = [
  "apple", "Asia", "car", "blue", "run", "table", "London", "quickly", "joy", "swim", "tall", "cat", "book"
];

// Get unique distractors up to 3, using fallback pool if necessary
export function getDistractors(pool, correct, moreDistractors = []) {
  // Remove blank/empty/null/undefined, ensure strings
  let base = pool.filter(w => !!w && typeof w === "string" && w.trim() !== "" && w !== correct);
  moreDistractors.forEach(w => {
    if (w && typeof w === "string" && w.trim() !== "" && w !== correct) base.push(w);
  });
  // Use Set to remove duplicates and the correct answer
  let distractors = Array.from(new Set(base));
  // Add fallback if not enough
  for (let word of DEFAULT_DISTRACTORS) {
    if (distractors.length >= 3) break;
    if (!distractors.includes(word) && word !== correct) distractors.push(word);
  }
  // Always exactly 3
  return shuffleArray(distractors).slice(0, 3);
}
